// ─── VER PAGOS ───────────────────────────────────────────────────────────────
// Lista de solo consulta con todos los pagos registrados (Cuentas por Pagar y
// Champtex). No modifica ningún dato ni cálculo: recibe los pagos ya armados por
// cada pestaña, permite filtrarlos por fechas y grupo, y exportarlos a Excel.
import { useMemo, useState } from "react";
import * as XLSX from "xlsx";

// "dd/mm/aaaa" → Date (o null si no trae fecha válida).
const parseFecha = (s) => {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(s || "").trim());
  if (!m) return null;
  const d = new Date(+m[3], +m[2] - 1, +m[1]);
  return isNaN(d.getTime()) ? null : d;
};
// "aaaa-mm-dd" de un <input type="date"> → Date local.
const parseISO = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || "");
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
};
const usd = (n) =>
  (Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const normalizar = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const inputCls =
  "border border-slate-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500";

/**
 * pagos: [{ fecha: "dd/mm/aaaa", grupo, monto, ...columnas }]
 * columnas: [{ key, label, align?: "right" | "center" }] (sin fecha ni monto)
 */
export default function VerPagos({ titulo, pagos, columnas, etiquetaGrupo, moneda = "USD", nota, placeholder = "Buscar…", archivo, onCerrar }) {
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [grupo, setGrupo] = useState("");
  const [busqueda, setBusqueda] = useState("");

  const grupos = useMemo(
    () => [...new Set(pagos.map((p) => p.grupo).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es")),
    [pagos]
  );

  const filtrados = useMemo(() => {
    const d0 = parseISO(desde);
    const d1 = parseISO(hasta);
    const q = normalizar(busqueda.trim());
    return pagos
      .map((p) => ({ ...p, _fecha: parseFecha(p.fecha) }))
      .filter((p) => !grupo || p.grupo === grupo)
      // Con rango de fechas, los pagos sin fecha (p. ej. anticipos) no entran.
      .filter((p) => (!d0 && !d1) || (p._fecha && (!d0 || p._fecha >= d0) && (!d1 || p._fecha <= d1)))
      .filter((p) => !q || columnas.some((c) => normalizar(p[c.key]).includes(q)) || normalizar(p.fecha).includes(q))
      // Más reciente primero; los que no traen fecha al final.
      .sort((a, b) => (b._fecha?.getTime() ?? -Infinity) - (a._fecha?.getTime() ?? -Infinity));
  }, [pagos, columnas, desde, hasta, grupo, busqueda]);

  const total = filtrados.reduce((s, p) => s + (Number(p.monto) || 0), 0);
  const hayFiltro = desde || hasta || grupo || busqueda.trim();

  const exportar = () => {
    const filas = filtrados.map((p) => {
      const fila = { Fecha: p.fecha || "" };
      for (const c of columnas) fila[c.label] = p[c.key] ?? "";
      fila[`Monto ${moneda}`] = Number(p.monto) || 0;
      return fila;
    });
    filas.push({});
    filas.push({ Fecha: "TOTAL", [`Monto ${moneda}`]: total });
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Pagos");
    const hoy = new Date().toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" }).replace(/\//g, "-");
    XLSX.writeFile(wb, `${archivo} ${hoy}.xlsx`);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start sm:items-center justify-center p-3 overflow-y-auto"
      onClick={onCerrar}
      data-testid="ver-pagos-modal"
    >
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-5xl p-5 space-y-3 my-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider">{titulo}</p>
            <p className="text-sm text-slate-600 mt-0.5">
              {filtrados.length} {filtrados.length === 1 ? "pago" : "pagos"}
              {hayFiltro ? ` (de ${pagos.length})` : ""} · Total{" "}
              <span className="font-bold text-slate-800" data-testid="ver-pagos-total">
                {usd(total)} {moneda}
              </span>
            </p>
            {nota && <p className="text-[11px] text-slate-400 mt-0.5">{nota}</p>}
          </div>
          <button onClick={onCerrar} className="text-slate-400 hover:text-slate-600 text-2xl leading-none px-2" title="Cerrar">
            ×
          </button>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-slate-500 flex flex-col">
            Desde
            <input type="date" className={inputCls} value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="ver-pagos-desde" />
          </label>
          <label className="text-xs text-slate-500 flex flex-col">
            Hasta
            <input type="date" className={inputCls} value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="ver-pagos-hasta" />
          </label>
          {grupos.length > 1 && (
            <label className="text-xs text-slate-500 flex flex-col">
              {etiquetaGrupo}
              <select className={inputCls} value={grupo} onChange={(e) => setGrupo(e.target.value)} data-testid="ver-pagos-grupo">
                <option value="">Todos</option>
                {grupos.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </label>
          )}
          <label className="text-xs text-slate-500 flex flex-col flex-1 min-w-[180px]">
            Buscar
            <input className={inputCls} value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder={placeholder} data-testid="ver-pagos-buscar" />
          </label>
          {hayFiltro && (
            <button
              type="button"
              onClick={() => { setDesde(""); setHasta(""); setGrupo(""); setBusqueda(""); }}
              className="text-xs font-medium text-slate-500 px-2 py-2 hover:text-slate-700"
            >
              ✕ Limpiar
            </button>
          )}
          <button
            type="button"
            onClick={exportar}
            disabled={!filtrados.length}
            data-testid="ver-pagos-exportar"
            className="text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
          >
            📗 Exportar a Excel
          </button>
        </div>

        <div className="overflow-auto max-h-[60vh] border border-slate-200 rounded-xl">
          <table className="w-full text-xs">
            <thead className="sticky top-0">
              <tr className="bg-slate-50 text-slate-500">
                <th className="text-left px-3 py-2 font-semibold">Fecha</th>
                {columnas.map((c) => (
                  <th key={c.key} className={`px-3 py-2 font-semibold ${c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "text-left"}`}>
                    {c.label}
                  </th>
                ))}
                <th className="text-right px-3 py-2 font-semibold">Monto {moneda}</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((p, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-3 py-2 whitespace-nowrap">{p.fecha || "—"}</td>
                  {columnas.map((c) => (
                    <td key={c.key} className={`px-3 py-2 ${c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : ""}`}>
                      {p[c.key] === "" || p[c.key] == null ? "—" : p[c.key]}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right whitespace-nowrap font-semibold text-slate-800">{usd(p.monto)}</td>
                </tr>
              ))}
              {filtrados.length === 0 && (
                <tr>
                  <td colSpan={columnas.length + 2} className="text-center text-slate-400 py-6">
                    {pagos.length === 0 ? "Todavía no hay pagos registrados." : "Ningún pago coincide con los filtros."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
